"use client";
import { useSignIn, useUser } from "@clerk/nextjs";
import { Loader } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";
import { AuthCustomInput } from "../../../../app/components/AuthCustomInput";
import { Button } from "../../../../app/components/CustomButton";
import GoogleIcon from "../../../../app/public/icons/google.svg";

const LoginForm = () => {
	const router = useRouter();
	const { isSignedIn, isLoaded } = useUser();
	const { signIn } = useSignIn();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This is a false positive
	useEffect(() => {
		if (isSignedIn && isLoaded) {
			router.push("/overview");
		}
	}, [isLoaded, isSignedIn]);

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();

		setError(null);
		setIsLoading(true);

		try {
			const { error } = await signIn.password({ identifier: email, password });
			if (error) {
				if (error.code === "session_exists") {
					window.location.reload();
				} else {
					setError(error.message || "Something went wrong. Please try again.");
				}
				return;
			}

			if (signIn.status === "complete") {
				await signIn.finalize();
			}
			router.push("/overview");
		} finally {
			setIsLoading(false);
		}
	}

	async function signInWithOAuth(strategy: "oauth_apple" | "oauth_google") {
		setError(null);
		if (strategy === "oauth_google") {
			setIsGoogleLoading(true);
		}
		const { error } = await signIn.sso({
			strategy,
			redirectUrl: "/overview",
			redirectCallbackUrl: "/sign-in/sso-callback",
		});
		if (error) {
			setError(error.message || "OAuth sign-in failed.");
			setIsGoogleLoading(false);
		}
	}

	if (!isMounted || !isLoaded) {
		return (
			<div className="flex flex-col space-6" data-testid="login-form">
				<div className="flex w-full justify-center gap-6">
					<Loader className="h-[48px] w-[48px] animate-spin text-[#1A2314]" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col" data-testid="login-form">
			<form onSubmit={onSubmit}>
				<div className="grid gap-4">
					<div className="grid gap-4">
						<AuthCustomInput
							label="Email"
							type="email"
							placeholder="Enter your email"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
						<AuthCustomInput
							label="Password"
							type="password"
							placeholder="Enter your password"
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<div className="-mt-1 flex justify-end">
							<Link
								href="/forgot-password"
								className="text-sm font-medium text-[#1A2314] underline underline-offset-4 hover:text-[#6B7280]"
							>
								Forgot password?
							</Link>
						</div>
						<Button
							type="submit"
							className="h-11 w-full rounded-full !bg-[#004A7C] !text-white shadow-[0_1px_2px_rgba(10,13,18,0.05)] hover:!bg-[#003366]/90"
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<Loader className="mr-2 h-4 w-4 animate-spin text-white" />
									Logging in...
								</>
							) : (
								"Log in"
							)}
						</Button>
						{error && (
							<p className="text-center text-[#DC2626] text-sm">{error}</p>
						)}
					</div>
					<Button
						variant="outline"
						className="h-11 w-full rounded-full bg-white text-[#1A2314] ring-1 ring-[#D5D7DA] hover:bg-[#F5F5F0] hover:text-[#1A2314]"
						onClick={() => signInWithOAuth("oauth_google")}
						type="button"
						disabled={isGoogleLoading}
					>
						{isGoogleLoading ? (
							<Loader className="mr-2 h-4 w-4 animate-spin text-[#1A2314]" />
						) : (
							<Image
								src={GoogleIcon}
								alt="Google"
								width={16}
								height={16}
								className="mr-2"
							/>
						)}
						Continue with Google
					</Button>
				</div>
			</form>
		</div>
	);
};

export default LoginForm;
